import css from './UserManagement.module.css';
import { useState, useEffect, useContext } from 'react';
import { UserListItem } from '../../../types/appModels';
import { ErrorCode } from '../../../types/appTypes';
import {
  AdminDataTable,
  BooleanDataColumn,
  DataTableRow,
  DateDataColumn,
  TextDataColumn,
  SortColumn,
  SortDirection,
  DateRangeFilter,
  FilterColumn,
  OptionsDataFilter,
  ActionsColumn,
  TableStatus,
} from '../AdminDataTable/AdminDataTable';
import { useTableData } from '../../../hooks/useTableData';
import { UserContext } from '../../../context/userContext';
import { useNavigate } from 'react-router-dom';
import { 
  FiEdit2,
  FiCheckCircle,
  FiSlash,
  FiTrash2
} from 'react-icons/fi';
import NewRegistrationModal from '../NewRegistrationModal/NewRegistrationModal';
import ConfirmationModal from '../../Common/ConfirmationModal/ConfirmationModal';

interface UserDataListItem extends DataTableRow {
  name: string;
  email: string;
  company: string;
  role: string;
  userType: string;
  createdDate: Date;
  lastLoginDate: Date;
  enabled: boolean;
}

const UserManagement = () => {
  const { user } = useContext(UserContext);
  const nav = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { dataState, setSort, setFilters, runFilters, startAction, actionFailed, reload } =
    useTableData<UserListItem>({
      baseEndpoint: process.env.REACT_APP_API_USER!,
      initialData: { count: 0, list: [] },
      defaultSort: {
        columnId: 'createdDate',
        direction: SortDirection.Descending,
      },
    });
  const [showModal, setShowModal] = useState<{
    type: 'enable' | 'disable' | 'delete' | null;
    row: UserDataListItem | null;
  }>({ type: null, row: null });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const columns = [
    new TextDataColumn<UserDataListItem>('name', 'Name', true, true, 25,
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ),
    new TextDataColumn<UserDataListItem>('email', 'Email', true, true, 25, 
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ),
    new TextDataColumn<UserDataListItem>(
      'company',
      'Company',
      true,
      true,
      25,
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ),
    new TextDataColumn<UserDataListItem>(
      'role',
      'Role',
      true,
      true,
      25,
      (value) => (
        <div className={css.textCell}>{value}</div>
      ),
      undefined,
      undefined,
      (column, handleFilter) => {
        return (
          <OptionsDataFilter
            column={column}
            handleFilter={handleFilter}
            options={[
              { label: 'User', value: 'User' },
              { label: 'Manager', value: 'Manager' },
              { label: 'Admin', value: 'Admin' },
            ]}
          />
        );
      }
    ),
    ...(!isMobile ? [
      new TextDataColumn<UserDataListItem>(
        'userType',
        'User Type',
        true,
        true,
        25,
        (value) => (
          <div className={css.textCell}>{value}</div>
        ),
        undefined,
        undefined,
        (column, handleFilter) => {
          return (
            <OptionsDataFilter
              column={column}
              handleFilter={handleFilter}
              options={[
                { label: 'Lite', value: 'Lite' },
                { label: 'User', value: 'User' },
                { label: 'Ambassador', value: 'Ambassador' },
                { label: 'Manager', value: 'Manager' },
                { label: 'Admin', value: 'Admin' },
              ]}
            />
          );
        }
      ),
    ] : []),
    new DateDataColumn<UserDataListItem>(
      'createdDate',
      'Created (UTC)',
      true,
      true,
      25,
      (date) => (
        <div className={`${css.dateCell} ${css.hideOnMobile}`}>
          <div>{date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}</div>
          <div>{date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(',', '')}</div>
        </div>
      ),
      undefined,
      undefined,
      undefined,
      (column, handleFilter) => {
        return <DateRangeFilter column={column} handleFilter={handleFilter} />;
      }
    ),
    new DateDataColumn<UserDataListItem>(
      'lastLoginDate',
      'Last Login (UTC)',
      true,
      true,
      25,
      (date) => (
        <div className={`${css.dateCell} ${css.hideOnMobile}`}>
          <div>{date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}</div>
          <div>{date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(',', '')}</div>
        </div>
      ),
      undefined,
      undefined,
      undefined,
      (column, handleFilter) => {
        return <DateRangeFilter column={column} handleFilter={handleFilter} />;
      }
    ),
    new BooleanDataColumn<UserDataListItem>(
      'enabled', 
      'Enabled', 
      true, 
      true, 
      25,
      (value) => (
        <span className={`${css.statusBadge} ${value ? css.statusActive : css.statusInactive} ${css.hideOnMobile}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    ),
    new ActionsColumn<UserDataListItem>(
      'actions', 
      'Actions', 
      [
        {
          action: 'MANAGE',
          label: 'manage',
          allowed: (r) => r.id !== user?.id,
          icon: <FiEdit2 />,
        },
        {
          action: 'ENABLE',
          label: 'enable',
          allowed: (r) => !r.enabled && r.id !== user?.id,
          icon: <FiCheckCircle />,
        },
        {
          action: 'DISABLE',
          label: 'disable',
          allowed: (r) => r.enabled && r.id !== user?.id,
          icon: <FiSlash />,
        },
        {
          action: 'DELETE',
          label: 'delete',
          allowed: (r) => r.id !== user?.id,
          icon: <FiTrash2 />,
        },
      ],
      true,
      35 // Increased from 25 to 35 to give more space for the icons
    ),
  ];

  let rows: UserDataListItem[] = [];
  let count: number = 0;
  if (dataState.data !== undefined) {
    // format data
    rows = dataState
      .data!.list.filter((item) => item.mongoData.type !== 'server')
      .map<UserDataListItem>((item) => {
        return {
          name: item.profile.name,
          email: item.profile.email,
          id: item.mongoData._id,
          company: item.profile.company,
          role: item.profile.role,
          userType: item.profile.userType,
          createdDate: new Date(item.mongoData.creation_date * 1000),
          lastLoginDate: new Date(
            item.mongoData.last_authentication_date * 1000
          ),
          enabled: !item.mongoData.disabled,
        };
      });

    // sort data
    if (
      dataState.sort !== undefined &&
      dataState.sort.direction !== SortDirection.None
    ) {
      rows.sort((a, b) => {
        const vA = a[dataState.sort!.columnId];
        const vB = b[dataState.sort!.columnId];
        if (vA === vB) return 0;
        if (vA === undefined) return -1;
        if (vB === undefined) return 1;
        return vA < vB ? -1 : 1;
      });

      if (dataState.sort.direction === SortDirection.Descending) rows.reverse();
    }

    // filter data
    if (dataState.filters !== undefined && dataState.filters.length > 0) {
      console.log('Applying filters:', dataState.filters);
      for (const filter of dataState.filters) {
        console.log('Processing filter:', filter);
        if (filter.filter === undefined) {
          console.log('Skipping undefined filter');
          continue;
        }

        if (filter.columnId.endsWith('From')) {
          const fc = filter.columnId.replace('From', '');
          rows = rows.filter(
            (r) => r[fc]! >= new Date(Date.parse(filter.filter!))
          );
        } else if (filter.columnId.endsWith('To')) {
          const fc = filter.columnId.replace('To', '');
          rows = rows.filter(
            (r) => r[fc]! <= new Date(Date.parse(filter.filter!))
          );
        } else if (filter.columnId === 'enabled') {
          const fv = filter.filter === 'true';
          rows = rows.filter((r) => r['enabled'] === fv);
        } else if (filter.columnId === 'name' || filter.columnId === 'email' || filter.columnId === 'company') {
          // Case insensitive text search for name, email, and company
          const searchValue = filter.filter.toLowerCase();
          rows = rows.filter((r) => {
            const fieldValue = String(r[filter.columnId]).toLowerCase();
            return fieldValue.includes(searchValue);
          });
        } else {
          // Exact match for other fields like role
          rows = rows.filter((r) => r[filter.columnId] === filter.filter);
        }
      }
    }

    count = rows.length;
  }

  const handleSort = (sc: SortColumn | undefined) => {
    setSort(sc);
  };

  const handleFilter = (filters: FilterColumn[]) => {
    console.log('handleFilter called with:', filters);
    // If filters array is empty, clear all filters
    if (filters.length === 0) {
      console.log('Clearing all filters...');
      setFilters([]);
      // Force a data refresh when clearing filters
      runFilters([]);
      return;
    }
    setFilters(filters);
  };

  // Add logging for state changes
  useEffect(() => {
    console.log('Current dataState:', dataState);
    console.log('Current filters:', dataState.filters);
  }, [dataState]);

  const handleAdd = () => {
    setIsModalOpen(true);
  };

  const handleAction = (action: string, row: UserDataListItem) => {
    if (action === 'MANAGE') {
      handleManageAction(row);
    } else if (action === 'ENABLE') {
      handleEnableAction(row);
    } else if (action === 'DISABLE') {
      handleDisableAction(row);
    } else if (action === 'DELETE') {
      handleDeleteAction(row);
    }
  };

  const handleManageAction = (row: UserDataListItem) => {
    nav(`/admin/manageUser?did=${row.id}`);
  };

  const handleEnableAction = (row: UserDataListItem) => {
    setShowModal({ type: 'enable', row });
  };

  const handleConfirmEnable = async () => {
    if (!showModal.row) return;

    const endpoint = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_USER!}/${showModal.row.id}/enable`;

    startAction();
    fetch(`${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + user!.accessToken,
      },
    })
      .then((response) => {
        if (response.status === 204) {
          reload();
        } else if (response.status === 404) {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.notFound,
            message: 'The user could not be found or has already been enabled.',
          });
        } else {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.unknown,
            message: 'An error occurred and the user could not be enabled.',
          });
        }
      })
      .catch((err) => {
        actionFailed(TableStatus.Error, {
          code: ErrorCode.unknown,
          message: 'An error occurred and the user could not be enabled.',
        });
      })
      .finally(() => {
        setShowModal({ type: null, row: null });
      });
  };

  const handleDisableAction = (row: UserDataListItem) => {
    setShowModal({ type: 'disable', row });
  };

  const handleConfirmDisable = async () => {
    if (!showModal.row) return;

    const endpoint = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_USER!}/${showModal.row.id}/disable`;

    startAction();
    fetch(`${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + user!.accessToken,
      },
    })
      .then((response) => {
        if (response.status === 204) {
          reload();
        } else if (response.status === 404) {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.notFound,
            message: 'The user could not be found or has already been disabled.',
          });
        } else {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.unknown,
            message: 'An error occurred and the user could not be disabled.',
          });
        }
      })
      .catch((err) => {
        actionFailed(TableStatus.Error, {
          code: ErrorCode.unknown,
          message: 'An error occurred and the user could not be disabled.',
        });
      })
      .finally(() => {
        setShowModal({ type: null, row: null });
      });
  };

  const handleDeleteAction = (row: UserDataListItem) => {
    setShowModal({ type: 'delete', row });
  };

  const handleConfirmDelete = async () => {
    if (!showModal.row) return;

    const endpoint = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_USER!}/${showModal.row.id}`;

    startAction();
    fetch(`${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + user!.accessToken,
      },
    })
      .then((response) => {
        if (response.status === 204) {
          reload();
        } else if (response.status === 404) {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.notFound,
            message: 'The user could not be found or has already been deleted.',
          });
        } else {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.unknown,
            message: 'An error occurred and the user could not be deleted.',
          });
        }
      })
      .catch((err) => {
        actionFailed(TableStatus.Error, {
          code: ErrorCode.unknown,
          message: 'An error occurred and the user could not be deleted.',
        });
      })
      .finally(() => {
        setShowModal({ type: null, row: null });
      });
  };

  // Custom actions renderer
  const renderActionIcons = (row: UserDataListItem) => (
    <div className={css.actionsContainer}>
      {row.id !== user?.id && (
        <FiEdit2 
          className={`${css.actionButton} ${css.manageButton}`} 
          onClick={() => handleManageAction(row)}
          title="Manage user"
          size={18}
        />
      )}
      
      {!row.enabled && row.id !== user?.id && (
        <FiCheckCircle 
          className={`${css.actionButton} ${css.enableButton}`} 
          onClick={() => handleEnableAction(row)}
          title="Enable user"
          size={18}
        />
      )}
      
      {row.enabled && row.id !== user?.id && (
        <FiSlash 
          className={`${css.actionButton} ${css.disableButton}`} 
          onClick={() => handleDisableAction(row)}
          title="Disable user"
          size={18}
        />
      )}
      
      {row.id !== user?.id && (
        <FiTrash2 
          className={`${css.actionButton} ${css.deleteButton}`} 
          onClick={() => handleDeleteAction(row)}
          title="Delete user"
          size={18}
        />
      )}
    </div>
  );

  return (
    <div className={css.userManagementContainer}>
      <AdminDataTable
        columns={columns}
        rows={rows}
        total={count}
        allowFilter={true}
        allowNoSort={false}
        allowAdd={true}
        addLabel='New Registration'
        onSort={handleSort}
        onFilter={handleFilter}
        onAdd={handleAdd}
        onAction={handleAction}
        state={dataState}
        renderActionIcons={renderActionIcons}
      />
      <NewRegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
      {showModal.type === 'enable' && (
        <ConfirmationModal
          title="Enable User"
          message="Are you sure you want to enable this user?"
          confirmText="Enable"
          icon={<FiCheckCircle />}
          onConfirm={handleConfirmEnable}
          onCancel={() => setShowModal({ type: null, row: null })}
        />
      )}
      {showModal.type === 'disable' && (
        <ConfirmationModal
          title="Disable User"
          message="Are you sure you want to disable this user?"
          confirmText="Disable"
          icon={<FiSlash />}
          onConfirm={handleConfirmDisable}
          onCancel={() => setShowModal({ type: null, row: null })}
        />
      )}
      {showModal.type === 'delete' && (
        <ConfirmationModal
          title="Delete User"
          message="Deleting this user will remove the registration, profile, and user account from Mongo as well as Spotfire. This action cannot be undone. Are you sure you want to delete this user?"
          confirmText="Delete"
          icon={<FiTrash2 />}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowModal({ type: null, row: null })}
        />
      )}
    </div>
  );
};

export default UserManagement;